import { LensProperty, LensPropertyOption } from '@/models/webshop.model';
import { IOption } from '@/lib';
import { capitalizeBoolean } from './string.helper';

/**
 * Properties that should be excluded from user-facing forms
 * These are internal properties managed by the system
 */
const EXCLUDED_PROPERTIES: string[] = [];

/**
 * Checks if options appear to be numeric values
 * @param options - Array of options to check
 * @returns true if most options are numeric
 */
function areOptionsNumeric(options: IOption[]): boolean {
  if (options.length === 0) {
    return false;
  }
  const numericCount = options.filter((opt) => {
    const match = opt.label.match(/^[+-]?\d*\.?\d+/);
    return match !== null;
  }).length;
  // Consider numeric if at least 80% of options are numeric
  return numericCount / options.length >= 0.8;
}

/**
 * Checks if a property should have its options sorted numerically
 * Infers from property data - checks if options are numeric values
 * @param property - The lens property to check
 * @param options - The converted options array
 * @returns true if the property should be sorted numerically
 */
export function shouldSortNumerically(property: LensProperty, options: IOption[]): boolean {
  // Check if property has step range (indicates numeric values)
  if (hasStepRange(property)) {
    return true;
  }
  // Check if options appear to be numeric
  return areOptionsNumeric(options);
}

/**
 * Sorts options numerically from low to high
 * @param options - Array of options to sort
 * @returns Sorted array of options
 */
export function sortOptionsNumerically(options: IOption[]): IOption[] {
  return [...options].sort((a, b) => {
    // Extract numeric values from labels, handling negative numbers and decimals
    const aMatch = a.label.match(/^[+-]?\d*\.?\d+/);
    const bMatch = b.label.match(/^[+-]?\d*\.?\d+/);

    const aNum = aMatch ? parseFloat(aMatch[0]) : NaN;
    const bNum = bMatch ? parseFloat(bMatch[0]) : NaN;

    // If both are valid numbers, sort numerically
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }

    // If only one is a number, put the number first
    if (!isNaN(aNum)) {
      return -1;
    }
    if (!isNaN(bNum)) {
      return 1;
    }

    // If neither is a number, maintain original order
    return 0;
  });
}

/**
 * Checks if a property has step-based range configuration (used by MTO lenses)
 * @param property - The lens property to check
 * @returns true if the property has valid step range configuration
 */
export function hasStepRange(property: LensProperty): boolean {
  return (
    typeof property.stepMin === 'number' &&
    typeof property.stepMax === 'number' &&
    typeof property.stepValue === 'number' &&
    property.stepValue > 0
  );
}

/**
 * Checks if a value is valid within a step-based range (used by MTO lenses)
 * A value is valid if it falls within [stepMin, stepMax] and aligns with the step increments
 * @param property - The lens property with step range configuration
 * @param value - The value to validate (as string)
 * @returns true if the value is valid within the step range
 */
export function isValueInStepRange(property: LensProperty, value: string | null): boolean {
  if (value === null) {
    return false;
  }

  if (!hasStepRange(property)) {
    return false;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return false;
  }

  const { stepMin, stepMax, stepValue, precision } = property;

  // Check if value is within bounds
  if (numValue < stepMin! || numValue > stepMax!) {
    return false;
  }

  // Check if value aligns with step increments
  // Use precision to handle floating point comparison
  const steps = (numValue - stepMin!) / stepValue!;
  const roundedSteps = Math.round(steps);
  const expectedValue = stepMin! + roundedSteps * stepValue!;
  const roundedExpected = Number(expectedValue.toFixed(precision ?? 2));
  const roundedActual = Number(numValue.toFixed(precision ?? 2));

  return roundedExpected === roundedActual;
}

/**
 * Checks if a value is valid for a given lens property
 * Handles both discrete options and step-based ranges (MTO lenses)
 * @param property - The lens property to validate against
 * @param value - The value to validate (as string)
 * @returns true if the value is valid for this property
 */
export function isValueValidForProperty(property: LensProperty, value: string | null): boolean {
  if (value === null) {
    return false;
  }

  // First check step-based range (MTO lenses)
  if (hasStepRange(property)) {
    return isValueInStepRange(property, value);
  }

  // Fall back to discrete options check
  if (property.options && property.options.length > 0) {
    return property.options.some((opt) => String(opt.optionValue) === value);
  }

  return false;
}

/**
 * Generates options from step range (stepMin, stepMax, stepValue) for MTO lenses
 * @param property - The lens property with step range configuration
 * @param formatPowerFn - Optional function to format power values
 * @returns Array of IOption generated from the step range
 */
export function generateOptionsFromStepRange(
  property: LensProperty,
  formatPowerFn?: (value: string) => string
): IOption[] {
  if (!hasStepRange(property)) {
    return [];
  }

  const { stepMin, stepMax, stepValue, precision, name } = property;
  const options: IOption[] = [];

  // Use a small epsilon to handle floating point comparison issues
  const epsilon = stepValue! / 1000;

  for (let value = stepMin!; value <= stepMax! + epsilon; value += stepValue!) {
    // Round to specified precision to avoid floating point issues
    const roundedValue = Number(value.toFixed(precision));

    // Skip if we've exceeded stepMax due to floating point
    if (roundedValue > stepMax!) {
      break;
    }

    let label = roundedValue.toFixed(precision);

    // Format power values if this is a Power property
    if (name === 'Power' && formatPowerFn) {
      label = formatPowerFn(label);
    }

    options.push({
      value: String(roundedValue),
      label
    });
  }

  return options;
}

/**
 * Determines if a lens property should be displayed to the user
 * @param property - The lens property to check
 * @returns true if the property should be displayed, false otherwise
 */
export function shouldDisplayProperty(property: LensProperty): boolean {
  // Exclude hidden properties
  if (property.visibility === 'Hidden') {
    return false;
  }

  // Exclude internal properties
  if (EXCLUDED_PROPERTIES.includes(property.name)) {
    return false;
  }

  // Show if has discrete options OR step-based range (MTO lenses)
  const hasOptions = property.options && property.options.length > 0;
  const hasRange = hasStepRange(property);

  if (!hasOptions && !hasRange) {
    return false;
  }

  return true;
}

/**
 * Converts MLS lens property options to dropdown options
 * Handles both discrete options and step-based ranges (MTO lenses)
 * @param property - The lens property
 * @param formatPowerFn - Optional function to format power values
 * @returns Array of IOption for dropdown, sorted numerically for numeric properties
 */
export function convertPropertyToOptions(
  property: LensProperty,
  formatPowerFn?: (value: string) => string
): IOption[] {
  // First check for step-based range (MTO lenses)
  if (hasStepRange(property)) {
    const options = generateOptionsFromStepRange(property, formatPowerFn);
    // Step-based ranges are always numeric, sort them
    return sortOptionsNumerically(options);
  }

  // Fall back to discrete options
  if (!property.options || property.options.length === 0) {
    return [];
  }

  const options = property.options.map((option: LensPropertyOption) => {
    const valueStr = String(option.optionValue);
    let label = option.optionLabel;

    // Format power values if this is a Power property and formatter is provided
    if (property.name === 'Power' && formatPowerFn) {
      label = formatPowerFn(label);
    }

    // Capitalize boolean values (true/false -> True/False) for consistency
    label = capitalizeBoolean(label);

    return {
      value: valueStr,
      label
    };
  });

  // Sort numeric properties from low to high (dynamically detected)
  if (shouldSortNumerically(property, options)) {
    return sortOptionsNumerically(options);
  }

  return options;
}

/**
 * Gets the default value for a lens property based on defaultOptionId
 * @param property - The lens property
 * @returns The default value as string, or null if not found
 */
export function getDefaultPropertyValue(property: LensProperty): string | null {
  if (!property.options || property.options.length === 0) {
    return null;
  }

  const defaultOption = property.options.find((opt) => opt.id === property.defaultOptionId);
  return defaultOption ? String(defaultOption.optionValue) : null;
}

/**
 * Gets displayable lens properties filtered and ready for rendering
 * @param lensProperties - Array of lens properties from MLS
 * @returns Filtered array of properties that should be displayed
 */
export function getDisplayableLensProperties(lensProperties: LensProperty[] | null): LensProperty[] {
  if (!lensProperties) {
    return [];
  }
  return lensProperties.filter(shouldDisplayProperty);
}

/**
 * Gets the translation key for a lens property name
 * Converts property names like "FrontCylinder" to "frontcylinder" for translation lookup
 * @param propertyName - The lens property name
 * @returns The translation key
 */
export function getPropertyTranslationKey(propertyName: string): string {
  return `platform.webshop.${propertyName.toLowerCase()}`;
}

/**
 * Returns lens properties in the order provided by the MLS API
 * The API already provides properties in the correct display order
 * @param properties - Array of lens properties
 * @returns Array of lens properties in API order
 */
export function sortLensProperties(properties: LensProperty[] | null): LensProperty[] {
  if (!properties) {
    return [];
  }

  // Return properties in the order provided by the API
  // The MLS API provides lensProperties in the correct display order
  return [...properties];
}

/**
 * Finds a specific lens property by name
 * @param properties - Array of lens properties
 * @param name - Property name to find
 * @returns The lens property or null if not found
 */
export function findProperty(properties: LensProperty[] | null, name: string): LensProperty | null {
  if (!properties) {
    return null;
  }
  return properties.find((p) => p.name === name) || null;
}

/**
 * Gets the option label for a specific value in a property
 * Handles both discrete options and step-based ranges (MTO lenses)
 * @param property - The lens property
 * @param value - The value to find
 * @returns The option label or the value itself if not found
 */
export function getOptionLabel(property: LensProperty, value: string | null): string {
  if (!value) {
    return '';
  }

  // For step-based ranges, the value is the label (formatted number)
  if (hasStepRange(property)) {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return numValue.toFixed(property.precision);
    }
    return value;
  }

  // For discrete options
  if (!property.options) {
    return value;
  }

  const option = property.options.find((opt) => String(opt.optionValue) === value);
  if (!option) {
    return value;
  }

  const label = option.optionLabel;
  return capitalizeBoolean(label);
}
